import React, { useState } from 'react'
import { T, Spinner, Empty, pct } from './ui'
import { getNFLSim } from './api'
import { playText } from './plain'

// Sim Check (Pro) — an honest confidence audit, not a pick list. It replays the whole
// slate with correlated variance and flags the props where the board's independent math
// diverges most from the joint outcome (over- or under-confident). Read-only research.
// Compute-heavy on the backend, so it runs ONLY when the user taps "Run".

const PROP_LABEL = { receptions: 'Receptions', anytime_td: 'Anytime TD', pass_tds: 'Pass TDs',
  rec_yards: 'Rec Yds', rush_yards: 'Rush Yds', pass_yards: 'Pass Yds' }

export default function SimCheck({ season, week }) {
  const [d, setD] = useState(null)
  const [busy, setBusy] = useState(false)
  const run = () => { setBusy(true); getNFLSim(season, week).then(r => { setD(r); setBusy(false) }) }

  return (
    <div style={{ padding: '4px 16px 90px' }}>
      <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 12, lineHeight: 1.5 }}>
        A correlated Monte-Carlo replay of this week's slate vs. the board's independent props — it flags where our
        numbers may be <b style={{ color: T.text }}>over- or under-confident</b>. A research check on the model, not a pick list.
      </div>

      {!d ? (
        <button onClick={run} disabled={busy} style={{ width: '100%', padding: '13px 0', fontSize: 14, fontWeight: 800,
          background: busy ? T.card : T.green, color: busy ? T.muted : '#04140D', border: `1px solid ${T.green}`,
          borderRadius: 10, cursor: busy ? 'default' : 'pointer' }}>
          {busy ? 'Simulating the slate…' : `▶ Run simulation · Week ${week}`}
        </button>
      ) : d._sample ? (
        <Empty message="Simulation unavailable right now — check back once the week's board is live." />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {[[d.games, 'Games'], [d.props, 'Props'], [d.diverging_count || (d.top_divergences || []).length, 'Diverging']].map(([n, k], i) => (
              <div key={i} style={{ flex: 1, textAlign: 'center', background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 4px' }}>
                <div style={{ fontFamily: T.mono, fontWeight: 900, fontSize: 18, color: i === 2 ? T.amber : T.text }}>{n}</div>
                <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.05em', marginTop: 3, textTransform: 'uppercase' }}>{k}</div>
              </div>
            ))}
          </div>

          {/* per-market agreement */}
          {Object.keys(d.by_market || {}).length > 0 && (
            <>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: T.muted, letterSpacing: '0.08em', margin: '2px 2px 8px' }}>BOARD ⟷ SIM AGREEMENT</div>
              <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
                {Object.entries(d.by_market).map(([mkt, m], i) => (
                  <div key={mkt} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                    <span style={{ fontSize: 12.5, fontWeight: 700 }}>{PROP_LABEL[mkt] || mkt}</span>
                    <span style={{ fontSize: 11, color: T.muted }}>
                      {m.props} props · <span style={{ fontFamily: T.mono, color: m.mean_abs_gap_pp <= 3 ? T.green : m.mean_abs_gap_pp <= 6 ? T.amber : T.red }}>{m.mean_abs_gap_pp}pp gap</span>
                      {m.diverging ? <span style={{ color: T.amber }}> · {m.diverging} flagged</span> : ''}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* biggest divergences */}
          <div style={{ fontSize: 10.5, fontWeight: 800, color: T.muted, letterSpacing: '0.08em', margin: '2px 2px 8px' }}>BIGGEST DIVERGENCES</div>
          {!(d.top_divergences || []).length ? (
            <Empty message="No meaningful divergences — the board and the correlated sim agree this week." />
          ) : (d.top_divergences || []).slice(0, 25).map((r, i) => {
            const boardOver = r.board_prob_over != null ? r.board_prob_over : 0
            const simOver = r.sim_prob_over != null ? r.sim_prob_over : 0
            const richer = simOver > boardOver     // sim likes the over MORE than the board
            return (
              <div key={i} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 11, padding: '10px 12px', marginBottom: 7 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                    <div style={{ fontSize: 10.5, color: T.muted }}>{r.team} · {playText(r.prop_type, r.line)}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, fontFamily: T.mono, fontSize: 11.5 }}>
                    <div style={{ color: T.muted2 }}>board {pct(boardOver)} <span style={{ color: T.faint }}>→</span> sim <b style={{ color: richer ? T.green : T.red }}>{pct(simOver)}</b></div>
                    <div style={{ fontSize: 10, color: T.amber, marginTop: 2 }}>{richer ? 'sim more bullish' : 'sim more bearish'} · {Math.round((r.gap || 0) * 100)}pp</div>
                  </div>
                </div>
              </div>
            )
          })}

          <button onClick={run} disabled={busy} style={{ width: '100%', marginTop: 6, padding: '10px 0', fontSize: 12.5, fontWeight: 700,
            background: T.card2, color: T.muted, border: `1px solid ${T.border}`, borderRadius: 9, cursor: busy ? 'default' : 'pointer' }}>
            {busy ? 'Re-running…' : '↻ Re-run'}
          </button>
        </>
      )}
    </div>
  )
}
